import React, { useState } from 'react';
import InputParameterRow from './InputParameterRow';
import {TInputParameter, TUpsertISRProjectDataModelsInput} from "../../Pages/DefineModel/model";
import DeleteProjectModal from "../DeleteProjectModal";
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';

type InputParameterTableProps = {
    parameters: TInputParameter[];
    onAddParameter: () => void;
    onDeleteParameter: (parameterId: string) => void;
    onUpdateParameter: (parameterId: string, updates: Partial<TInputParameter>) => void;
    onOpenFieldSelector: (parameterId: string) => void;
    validateParameterName: (name: string, currentId: string) => string | null;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
}

const InputParameterTable = ({
                                 parameters,
                                 onAddParameter,
                                 onDeleteParameter,
                                 onUpdateParameter,
                                 onOpenFieldSelector,
                                 validateParameterName,
                                 userProjectDataModels
                             }: InputParameterTableProps) => {
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

    const handleDeleteClick = (parameterId: string) => {
        setDeleteConfirmId(parameterId);
    };

    const parameterToDelete = parameters.find(p => p.id === deleteConfirmId);

    return (
        <div id="input-parameter-table-container" className="tw-max-w-4xl">
            {/* Header with Add Parameter button */}
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">

                <Typography variant="title-header" className="tw-text-grey-900">
                    Model Parameters
                </Typography>

                <Button id="input-parameter-add-btn" variant="primary" onClick={onAddParameter} className="!tw-rounded-[4px]">
                    Add Parameter
                </Button>
            </div>

            {/* Table */}
            {parameters.length > 0 ? (
                <div id="input-parameter-table-wrapper" className="tw-bg-grey-100 tw-rounded-sm tw-p-5">
                    <div className="tw-overflow-x-auto">
                        <table id="input-parameter-table" className="tw-w-full tw-border-0 tw-table-fixed">
                            <thead>
                                <tr>
                                    <th className="tw-px-5 tw-py-3 tw-text-left tw-w-[35%]">
                                        <Typography variant="sub-body-supporting" className="tw-text-grey-900">
                                            Parameter Name
                                        </Typography>
                                    </th>
                                    <th className="tw-w-[60px]"></th>
                                    <th className="tw-px-4 tw-py-3 tw-text-left tw-w-[50%]">
                                        <Typography variant="sub-body-supporting" className="tw-text-grey-900">
                                            Mapped to
                                        </Typography>
                                    </th>
                                    <th className="tw-px-4 tw-py-3 tw-w-[50px]"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {parameters.map((parameter) => (
                                    <InputParameterRow
                                        key={parameter.id}
                                        parameter={parameter}
                                        onDelete={handleDeleteClick}
                                        onUpdate={onUpdateParameter}
                                        onOpenFieldSelector={onOpenFieldSelector}
                                        validateParameterName={validateParameterName}
                                        userProjectDataModels={userProjectDataModels}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div id="input-parameter-empty-state" className="tw-bg-grey-100 tw-rounded-sm tw-p-8 tw-text-center">
                    <svg className="tw-mx-auto tw-h-12 tw-w-12 tw-text-grey-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <Typography variant="title" className="tw-mt-2 tw-text-grey-900">
                        No input parameters
                    </Typography>
                    <Typography variant="body-primary" className="tw-mt-1 tw-text-grey-600">
                        Get started by adding a model parameter to map URL parameters to fields.
                    </Typography>
                    <div className="tw-mt-4 tw-flex tw-justify-center">
                        <Button id="input-parameter-add-btn-empty" variant="secondary" onClick={onAddParameter}>
                            Add Parameter
                        </Button>
                    </div>
                </div>
            )}

            <DeleteProjectModal
                showModal={deleteConfirmId}
                closeModal={() => setDeleteConfirmId(null)}
                loading={false}
                onConfirm={async () => {
                    if (deleteConfirmId) {
                        onDeleteParameter(deleteConfirmId);
                        setDeleteConfirmId(null);
                    }
                }}
                deleteItem={{ name: parameterToDelete?.paramKey || "Input Parameter" }}
            />
        </div>
    );
};

export default InputParameterTable;
